import styled from 'styled-components';
import { capitalize } from 'lodash';
import Popper from '@mui/material/Popper';
import { FormEvent, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { Button, Grow, Paper } from '@mui/material';
import {
  blueCharcoal_2,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';

interface Props<T> {
  readonly options: readonly T[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}
export default function BaseMenuSelect<T extends string>({
  options,
  value,
  onChange,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: FormEvent<EventTarget>) => {
    const target = event.currentTarget as HTMLInputElement;
    if (target.textContent !== null) {
      onChange(target.textContent as T);
    }
    setOpen(false);
  };
  const suffixIconDown = <i className="material-icons">arrow_drop_down</i>;
  const suffixIconUp = <i className="material-icons">arrow_drop_up</i>;
  return (
    <Container>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        {capitalize(value)} {open ? suffixIconUp : suffixIconDown}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList autoFocusItem={open} id="menu-list-grow">
                  {options.map((item) => (
                    <MenuItem key={item} onClick={handleClose} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Container>
  );
}

const Container = styled.div`
  border-radius: 3px;
  color: white;
  background-color: black;
  display: grid;
  padding: 2px 0;
  > div {
    z-index: 100;
  }
  .material-icons {
    color: ${paradigmLightBlue};
  }
  .MuiPaper-root {
    border-radius: 10px;
  }
  .MuiButton-label {
    text-transform: none;
    color: white;
    font-size: 1rem;
    letter-spacing: 0;
    line-height: 1rem;
  }
  .MuiList-root {
    width: 290px;
    border-radius: 7px;
    background-color: ${blueCharcoal_2};
    box-shadow: 0 0 20px 0 rgba(74, 28, 186, 0.3);
    display: grid;
    align-items: center;
  }
  .MuiMenuItem-root {
    margin: auto;
    opacity: 0.7;
    color: white;
    font-size: 1rem;
    letter-spacing: 0;
    line-height: 1rem;
    text-transform: capitalize;
    text-align: center;
  }
  .MuiButtonBase-root {
    text-transform: capitalize;
    color: white;
    font-size: 1rem;
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }
`;
